import {FormEvent, useId, useMemo, useState} from "react";
import {Badge, Button, Form, InputGroup, Modal, Table} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {LicensesApi} from "../../api/LicensesApi";
import {UsersApi} from "../../api/UsersApi";
import {ProductsApi} from "../../api/ProductsApi";
import {EarlyAccessUserClaim, Editions, LicenseKind, ProductViewModel, UserViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {SubmitButton} from "../../components/SubmitButton";
import {Field, FieldControl, FieldSelect} from "../../components/Field";
import {formatDate} from "../../components/format";
import {SortHeader, useSort} from "../../components/sortable";
import {isReviewLicense, reviewEndsSoon} from "../../components/reviewLicense";
import {usePersistentState} from "../../components/usePersistentState";

const isString = (value: unknown): value is string => typeof value === 'string';
const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

// Licenses for every product.
export function LicensesPage() {
    const navigate = useNavigate();
    const {data, error, loading} = useAsync(() => Promise.all([LicensesApi.getAll(), UsersApi.getAll(), ProductsApi.getAll()]), []);
    const [filter, setFilter] = usePersistentState('admin-licenses:filter', '', isString);
    const [hideRevoked, setHideRevoked] = usePersistentState('admin-licenses:hide-revoked', false, isBoolean);
    const [creating, setCreating] = useState(false);
    const [licenses, users, products] = data ?? [];

    const userNames = useMemo(() => new Map(users?.map(x => [x.username, x.displayName]) ?? []), [users]);
    const shown = useMemo(() => {
        const text = filter.trim().toLowerCase();
        return (licenses ?? [])
            .filter(x => !hideRevoked || !x.revoked)
            .filter(x => !text || [x.key, x.productName, x.licensee, x.edition, x.ownerUsername, userNames.get(x.ownerUsername) ?? '']
                .some(field => field.toLowerCase().includes(text)))
            .sort((l, r) => r.createdOn.localeCompare(l.createdOn));
    }, [licenses, filter, hideRevoked, userNames]);
    const {sorted, sort, toggle} = useSort('admin-licenses:sort', shown, {
        key: x => x.key,
        product: x => x.productName,
        licensee: x => x.licensee,
        edition: x => x.edition,
        owner: x => x.ownerUsername ? (userNames.get(x.ownerUsername) ?? x.ownerUsername) : '',
        seats: x => x.seatsInUse,
        created: x => x.createdOn
    });
    const revokedCount = useMemo(() => (licenses ?? []).filter(x => x.revoked).length, [licenses]);

    return (
        <>
            <PageHeader title="Licenses">
                <Button onClick={() => setCreating(true)} disabled={!users || !products}><i className="bi bi-plus-lg me-1"></i>New license</Button>
            </PageHeader>
            <ErrorAlert error={error}/>
            {loading && !data && <LoadingSpinner/>}
            {licenses && (
                <>
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                        <InputGroup style={{ maxWidth: '24rem' }}>
                            <InputGroup.Text><i className="bi bi-search" aria-hidden="true"></i></InputGroup.Text>
                            <Form.Control placeholder="Filter by key, product, licensee or owner" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filter"/>
                        </InputGroup>
                        <Form.Check type="checkbox" id="hide-revoked" checked={hideRevoked} onChange={e => setHideRevoked(e.target.checked)}
                                    label={<>Hide revoked licenses{hideRevoked && revokedCount > 0 && <span className="text-body-secondary"> ({revokedCount} hidden)</span>}</>}/>
                    </div>
                    <Table responsive hover>
                        <thead>
                        <tr>
                            <SortHeader column="key" sort={sort} onSort={toggle}>Key</SortHeader>
                            <SortHeader column="product" sort={sort} onSort={toggle}>Product</SortHeader>
                            <SortHeader column="licensee" sort={sort} onSort={toggle}>Licensee</SortHeader>
                            <SortHeader column="edition" sort={sort} onSort={toggle}>Edition</SortHeader>
                            <SortHeader column="owner" sort={sort} onSort={toggle}>Owner</SortHeader>
                            <SortHeader column="seats" sort={sort} onSort={toggle}>Seats</SortHeader>
                            <SortHeader column="created" sort={sort} onSort={toggle}>Created</SortHeader>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {sorted.map(license => (
                            <tr key={license.id} className={license.revoked ? 'clickable-row license-revoked' : 'clickable-row'} onClick={() => navigate(`/admin/licenses/${license.id}`)}>
                                <td className="text-nowrap">
                                    <span className="license-key">{license.key}</span>
                                    {license.kind === 'earlyAccess' && <Badge bg="info" text="dark" className="ms-2" title="Activates only Early Access Versions">Early Access</Badge>}
                                    {isReviewLicense(license) && <Badge bg="secondary" className="ms-2" title="A reviewer's, from a press inquiry: not a sale">Review</Badge>}
                                    {reviewEndsSoon(license) && <Badge bg="warning" text="dark" className="ms-2">Ends {formatDate(license.reviewExpiresOn)}</Badge>}
                                    {license.revoked && <Badge bg="danger" className="ms-2">Revoked</Badge>}
                                </td>
                                <td>{license.productName || <span className="text-body-secondary">None</span>}</td>
                                <td>{license.licensee}</td>
                                <td>{license.kind === 'earlyAccess' || isReviewLicense(license) ? <span className="text-body-secondary">n/a</span> : license.edition}</td>
                                <td>{license.ownerUsername ? (userNames.get(license.ownerUsername) ?? license.ownerUsername) : <span className="text-body-secondary">None</span>}</td>
                                <td>{license.seatsInUse} / {license.seats}</td>
                                <td>{formatDate(license.createdOn)}</td>
                                <td className="text-end">
                                    <Link to={`/admin/licenses/${license.id}`} onClick={e => e.stopPropagation()}>Open</Link>
                                </td>
                            </tr>
                        ))}
                        {shown.length === 0 && <tr><td colSpan={8} className="text-center text-body-secondary py-4">No licenses{(filter || hideRevoked) && ' match the filter'}.</td></tr>}
                        </tbody>
                    </Table>
                </>
            )}
            {users && products && <CreateLicenseModal show={creating} users={users} products={products} onHide={() => setCreating(false)}
                                          onCreated={id => navigate(`/admin/licenses/${id}`)}/>}
        </>
    );
}

// An Early Access license's owner must be an Early Access User: its list has only those, and no "No owner".
export const isEarlyAccessUser = (user: UserViewModel) => !!user.claims?.includes(EarlyAccessUserClaim);

export function OwnerSelect({users, value, onChange, id, earlyAccess}: { users: UserViewModel[], value: string, onChange(value: string): void, id: string, earlyAccess?: boolean }) {
    const sorted = users.filter(x => !earlyAccess || isEarlyAccessUser(x)).sort((l, r) => l.displayName.localeCompare(r.displayName));
    return (
        <FieldSelect id={id} value={value} onChange={e => onChange(e.target.value)}>
            <option value="">{earlyAccess ? 'Choose an Early Access User' : 'No owner'}</option>
            {sorted.map(x => <option key={x.username} value={x.username}>{x.displayName} ({x.username})</option>)}
            {value && !sorted.some(x => x.username === value) && <option value={value}>{value} ({users.some(x => x.username === value) ? 'not an Early Access User' : 'no such user'})</option>}
        </FieldSelect>
    );
}

// Both selects take their validity and descriptions from the Field around them.
export function ProductSelect({products, value, onChange, id}: { products: ProductViewModel[], value: string, onChange(value: string): void, id: string }) {
    const sorted = [...products].sort((l, r) => l.name.localeCompare(r.name));
    return (
        <FieldSelect id={id} value={value} onChange={e => onChange(e.target.value)}>
            {!value && <option value="">Choose a product</option>}
            {sorted.map(x => <option key={x.id} value={x.id}>{x.name} ({x.sku}){x.published ? '' : ', unpublished'}</option>)}
            {value && !products.some(x => x.id === value) && <option value={value}>{value} (no such product)</option>}
        </FieldSelect>
    );
}

function CreateLicenseModal({show, users, products, onHide, onCreated}: { show: boolean, users: UserViewModel[], products: ProductViewModel[], onHide(): void, onCreated(id: string): void }) {
    const titleId = useId();
    const [productId, setProductId] = useState(products.length === 1 ? products[0].id : '');
    const [licensee, setLicensee] = useState('');
    const [kind, setKind] = useState<LicenseKind>('standard');
    const [edition, setEdition] = useState(Editions[0]);
    const [owner, setOwner] = useState('');
    const [key, setKey] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    // Picking an owner fills in the licensee, until it's typed by hand.
    const pickOwner = (username: string) => {
        const previous = users.find(x => x.username === owner)?.displayName ?? '';
        if (!licensee || licensee === previous) {
            setLicensee(users.find(x => x.username === username)?.displayName ?? '');
        }
        setOwner(username);
    };

    const earlyAccess = kind === 'earlyAccess';
    const pickKind = (value: LicenseKind) => {
        setKind(value);
        if (value === 'earlyAccess' && owner && !users.some(x => x.username === owner && isEarlyAccessUser(x))) {
            pickOwner('');
        }
    };
    const ownerMissing = earlyAccess && !owner;

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!licensee.trim() || !productId || ownerMissing) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const created = await LicensesApi.create({
                productId,
                licensee: licensee.trim(),
                // An Early Access license has no edition of its own to choose.
                edition: earlyAccess ? Editions[0] : edition,
                ownerUsername: owner,
                revoked: false,
                kind,
                key: key.trim() || undefined
            });
            onCreated(created.id);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show={show} onHide={onHide} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title id={titleId}>New license</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} shownFields={['productId', 'licensee', 'ownerUsername']}/>
                    <Form.Group className="mb-3" controlId="new-license-kind">
                        <Form.Label>Kind</Form.Label>
                        <Form.Select value={kind} aria-describedby="new-license-kind-help" onChange={e => pickKind(e.target.value as LicenseKind)}>
                            <option value="standard">Standard</option>
                            <option value="earlyAccess">Early Access</option>
                        </Form.Select>
                        <Form.Text id="new-license-kind-help">
                            {earlyAccess
                                ? 'For an Early Access User: activates only Early Access Versions, on 5 computers, 25 times a year. Their standard license is counted separately.'
                                : 'Activates the released versions, on 2 computers, 5 times a year.'}
                        </Form.Text>
                    </Form.Group>
                    <Field className="mb-3" controlId="new-license-product" label="Product" error={(validated && !productId && 'Choose a product.') || fieldError(error, 'productId')}
                           help="A generated key starts with the product's key prefix.">
                        <ProductSelect id="new-license-product" products={products} value={productId} onChange={setProductId}/>
                    </Field>
                    <Field className="mb-3" controlId="new-license-owner" label="Owner" help="The owner sees the license under My licenses and can free its seats."
                           error={(validated && ownerMissing && 'Choose an Early Access User.') || fieldError(error, 'ownerUsername')}>
                        <OwnerSelect id="new-license-owner" users={users} value={owner} onChange={pickOwner} earlyAccess={earlyAccess}/>
                    </Field>
                    <Field className="mb-3" controlId="new-license-licensee" label="Licensee" error={(validated && !licensee.trim() && 'Enter who the license is for.') || fieldError(error, 'licensee')}
                           help="Shown in the product's license dialog.">
                        <FieldControl value={licensee} onChange={e => setLicensee(e.target.value)}/>
                    </Field>
                    {!earlyAccess && (
                        <Form.Group className="mb-3" controlId="new-license-edition">
                            <Form.Label>Edition</Form.Label>
                            <Form.Select value={edition} onChange={e => setEdition(e.target.value)}>
                                {Editions.map(x => <option key={x}>{x}</option>)}
                            </Form.Select>
                        </Form.Group>
                    )}
                    <Form.Group controlId="new-license-key">
                        <Form.Label>Key <span className="text-body-secondary">(optional)</span></Form.Label>
                        <Form.Control className="license-key" placeholder="Generated if left empty" value={key} onChange={e => setKey(e.target.value)}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Create license</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
